import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Input,
  Spinner,
  Label,
  FormGroup,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaCheckCircle,
  FaClock,
  FaTimes,
} from "react-icons/fa";
import { FaReceipt } from "react-icons/fa";
import { reglementActions } from "../../sagas/reglementSlice";
import { formatPrice } from "../../helpers/utility";
import { useLocation, useNavigate } from "react-router-dom";
import { clientActions } from "../../sagas/clientSlice";
import { debounce } from "lodash";
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import { soldeActions } from "../../sagas/soldeSlice";
import {
  CBL_PAGE,
  CPMT_PRODUCTS_PAGE,
  CPMT_RECEPTION_PAGE,
  CPMT_STOCK_PAGE,
  PAY_FORM_EDIT,
  PAY_FORM_NEW,
  PAY_PAGE,
  POS_PAGE,
  RETOUR_FORM_NEW,
  RETOUR_PAGE,
} from "../../routes/routeConstants";
import POSHeader from "./POSHeader";
import POSKeypadModal from "./POSKeypadModal";

import "./AvanceClientListPOS.css";

const AvanceClientList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { loading, error, list } = useSelector((state) => state.reglements);
  const { clients } = useSelector((state) => state.clients);
  const { company, businessName, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState("AVANCE");

  // Dates default : current month
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const pad = (n) => n.toString().padStart(2, "0");
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  // Filters
  const [filters, setFilters] = useState({
    clientId: null,
    chantierId: null,
    dateFrom: formatDate(startOfMonth),
    dateTo: formatDate(endOfMonth),
    statut: null,
    query: "",
  });

  // POS Search keypad
  const searchInputRef = useRef(null);
  const [showSearchKeypad, setShowSearchKeypad] = useState(false);

  useEffect(() => {
    // F2 focus search
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        setShowSearchKeypad(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    dispatch(reglementActions.avanceClient(filters));
    if (!clients || clients.length === 0) {
      dispatch(clientActions.search({ query: "" }));
    }
  }, [dispatch, filters]);

  const handleReload = () => {
    dispatch(reglementActions.avanceClient(filters));
  };

  const handleEdit = (reglement) => {
    navigate(PAY_FORM_EDIT, { state: { reglement } });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        dispatch(
          reglementActions.avanceClient({
            ...filters,
            query,
          })
        );
      }, 250),
    [dispatch, filters]
  );

  // =========================
  // SOLDE BEFORE/AFTER helpers
  // =========================
  const parseAnyDate = (v) => {
    if (!v) return null;
    if (typeof v === "string") {
      if (/^\d{4}-\d{2}-\d{2}/.test(v)) return new Date(v + "T00:00:00");
      if (/^\d{2}-\d{2}-\d{4}$/.test(v)) {
        const [dd, mm, yyyy] = v.split("-").map(Number);
        return new Date(yyyy, mm - 1, dd);
      }
    }
    if (v instanceof Date) return v;
    return new Date(v);
  };

  const normalizeRef = (s) => (s || "").toString().trim().toUpperCase();
  const isSameRef = (a, b) => normalizeRef(a) === normalizeRef(b);

  const getReglementSignedAmount = (reglement) => {
    const montant = Number(reglement?.montantRegle || 0);
    const type = (reglement?.typeReglement || "").toString().toUpperCase();
    if (type === "AVANCE" || type === "PAIEMENT" || type === "REGLEMENT") {
      return { credit: montant, debit: 0 };
    }
    if (type === "BON_LIVRAISON" || type === "BL") {
      return { credit: 0, debit: montant };
    }
    return { credit: 0, debit: 0 };
  };

  const computeSoldeAvantApresFromMouvements = (reglement, mouvements = []) => {
    if (!mouvements || mouvements.length === 0) {
      return { soldeAvant: 0, soldeApres: 0 };
    }

    const refReglement = normalizeRef(reglement?.reference || reglement?.id);
    const mvtReglement = mouvements.find((m) => isSameRef(m?.reference, refReglement));

    if (mvtReglement) {
      return {
        soldeAvant: Number(mvtReglement.soldeAvant || 0),
        soldeApres: Number(mvtReglement.soldeApres || 0),
      };
    }

    const dReglement = parseAnyDate(reglement?.dateReglement);
    const sorted = [...mouvements].sort((a, b) => {
      const da = parseAnyDate(a?.dateOperation || a?.date_mouvement);
      const db = parseAnyDate(b?.dateOperation || b?.date_mouvement);
      return da - db;
    });

    let lastBefore = null;
    for (const m of sorted) {
      const dm = parseAnyDate(m?.dateOperation || m?.date_mouvement);
      if (!dm || !dReglement) continue;
      if (dm.getTime() <= dReglement.getTime()) lastBefore = m;
    }

    const baseAvant = lastBefore ? Number(lastBefore.soldeApres || 0) : 0;
    const { credit, debit } = getReglementSignedAmount(reglement);
    const soldeApres = baseAvant + Number(credit || 0) - Number(debit || 0);

    return { soldeAvant: baseAvant, soldeApres };
  };

  const getClientSoldeForReglement = (dispatch, reglement) => {
    return new Promise((resolve) => {
      if (!reglement?.clientId) {
        resolve({ soldeAvant: 0, soldeApres: 0 });
        return;
      }

      dispatch(
        soldeActions.search({
          clientId: reglement.clientId,
          dateMouvementTo: reglement.dateReglement,
          typeDocument: "",
          onSuccess: (rows) => {
            try {
              const { soldeAvant, soldeApres } = computeSoldeAvantApresFromMouvements(reglement, rows || []);
              resolve({ soldeAvant, soldeApres });
            } catch (e) {
              resolve({ soldeAvant: 0, soldeApres: 0 });
            }
          },
          onError: () => resolve({ soldeAvant: 0, soldeApres: 0 }),
        })
      );
    });
  };

  // =========================
  // RECEIPT (your real logic)
  // =========================
  const handleGenerateReceipt = async (reglement) => {
    try {
      const { soldeAvant, soldeApres } = await getClientSoldeForReglement(dispatch, reglement);

      const doc = buildPaymentReceiptDoc(reglement, soldeAvant, soldeApres, t, {
        showBeforeAfter: true,
        soldeApres,
        soldeAvant,
      });

      pdfMake.createPdf(doc).open();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: t("common.error"),
        text: t("reglement.errorGenerateReceipt") || "Erreur lors de la génération du reçu.",
      });
    }
  };

  const buildPaymentReceiptDoc = (reglement, soldeAvant, soldeApres, t, opts = {}) => {
    // ✅ keep your original pdf doc builder
    // (I kept it short here: paste your full version exactly)
    // ------------------------------------------------------
    const {
      devise = "MAD",
      showBeforeAfter = true,
      footerNote = null,
    } = opts || {};

    const primary = "#06912b";
    const ink = "#0F172A";
    const line = "#E2E8F0";
    const subtle = "#64748B";

    const safe = (v, fb = "-") => (v || v === 0 ? v : fb);

    const formatDateFR = (isoOrDate) => {
      if (!isoOrDate) return "-";
      try {
        const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
        return d.toLocaleDateString("fr-FR");
      } catch {
        return "-";
      }
    };

    const formatMoney = (n) => {
      const x = Number(n || 0);
      const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
      return `${s.replace(/\B(?=(\d{3})+(?!\d))/g, " ")} ${devise}`;
    };

    const paymentStatusLabel = (r) =>
      r?.statut ? t(`reglement.${r.statut.toLowerCase()}`) || r.statut : t("reglement.enAttente");

    const companyName = safe(company?.name, "Entreprise");
    const companyAddr = safe(company?.address, "");
    const companyTel = [company?.phone1, company?.phone2].filter(Boolean).join(" / ");
    const companyEmail = safe(company?.email, "");

    const metaRows = [
      [{ text: t("reglement.reference"), bold: true }, safe(reglement.reference || String(reglement.id))],
      [{ text: t("reglement.client"), bold: true }, safe(reglement.nomClient)],
      [{ text: t("reglement.dateReglement"), bold: true }, formatDateFR(reglement.dateReglement)],
      [{ text: t("reglement.modeReglement"), bold: true }, t(`reglement.${(reglement.modeReglement || "AUTRE").toLowerCase()}`) ],
      [{ text: t("reglement.montantRegle"), bold: true }, formatMoney(reglement.montantRegle)],
      [{ text: t("reglement.statut"), bold: true }, paymentStatusLabel(reglement)],
    ];

    const beforeAfterBlock =
      showBeforeAfter
        ? {
            table: {
              widths: ["33%", "34%", "33%"],
              body: [
                [
                  { text: "Solde avant", style: "th" },
                  { text: "Montant réglé", style: "th" },
                  { text: "Solde après", style: "th" },
                ],
                [
                  { text: formatMoney(soldeAvant), alignment: "center" },
                  { text: formatMoney(reglement.montantRegle), alignment: "center" },
                  { text: formatMoney(soldeApres), alignment: "center", bold: true },
                ],
              ],
            },
            layout: {
              fillColor: (rowIndex) => (rowIndex === 0 ? "#F8FAFC" : null),
              hLineColor: () => line,
              vLineColor: () => line,
              paddingLeft: () => 8,
              paddingRight: () => 8,
              paddingTop: () => 6,
              paddingBottom: () => 6,
            },
            margin: [0, 6, 0, 12],
          }
        : null;

    const politeNote =
      footerNote ||
      t("reglement.reminderLine") ||
      "Merci pour votre règlement. En cas de solde restant, veuillez régulariser dans les meilleurs délais.";

    return {
      pageSize: "A4",
      pageMargins: [32, 36, 32, 36],
      content: [
        {
          columns: [
            {
              stack: [
                { text: companyName, fontSize: 14, bold: true, color: ink, margin: [0, 0, 0, 2] },
                { text: companyAddr, fontSize: 9, color: subtle },
                companyTel ? { text: `Tél: ${companyTel}`, fontSize: 9, color: subtle } : null,
                companyEmail ? { text: `Email: ${companyEmail}`, fontSize: 9, color: subtle } : null,
              ].filter(Boolean),
            },
            {
              alignment: "right",
              stack: [
                { text: t("reglement.receiptTitle") || "Reçu de Paiement", fontSize: 16, bold: true, color: primary },
                { text: `# ${safe(reglement.reference || reglement.id, "-")}`, fontSize: 10, color: subtle },
                { text: formatDateFR(new Date()), fontSize: 9, color: subtle },
              ],
            },
          ],
          margin: [0, 0, 0, 12],
        },

        {
          table: { widths: ["35%", "*"], body: metaRows },
          layout: {
            hLineColor: () => line,
            vLineColor: () => line,
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6,
          },
          margin: [0, 0, 0, 12],
        },

        ...(beforeAfterBlock ? [beforeAfterBlock] : []),

        { text: politeNote, italics: true, fontSize: 10, color: subtle },
      ],
      styles: {
        th: { bold: true, color: ink, alignment: "center" },
      },
      defaultStyle: { fontSize: 10 },
    };
  };

  // =========================
  // UI helpers
  // =========================
  const getStatutBadge = (statut) => {
    const badges = {
      EN_ATTENTE: { color: "info", icon: FaClock },
      ENCAISSE: { color: "success", icon: FaCheckCircle },
      IMPAYE: { color: "danger", icon: FaTimes },
      ANNULE: { color: "warning", icon: FaTimes },
    };

    const badge = badges[statut] || { color: "secondary", icon: FaClock };
    const Icon = badge.icon;

    return (
      <Badge color={badge.color} className="pos-status-badge">
        <Icon className="me-1" size="12" />
        {t(`reglement.${statut.toLowerCase()}`)}
      </Badge>
    );
  };

  const getModeReglementIcon = (mode) => {
    const icons = {
      ESPECES: "💵",
      CHEQUE: "📝",
      VIREMENT: "🏦",
      CARTE_BANCAIRE: "💳",
      TRAITE: "📄",
      AUTRE: "❓",
    };
    return icons[mode] || "❓";
  };

  // KPIs
  const totalCount = list?.length || 0;
  const totalMontant = useMemo(() => {
    return (list || []).reduce((sum, r) => sum + (Number(r?.montantRegle) || 0), 0);
  }, [list]);

  // =========================
  // Render
  // =========================
  return (
    <Container fluid className="pos-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        fullName={fullName}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(POS_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_FORM_NEW);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
        }}
      />

      {/* ====== POS TOOLBAR ====== */}
      <Card className="pos-toolbar-card">
        <CardBody className="pos-toolbar-body">
          <div className="pos-toolbar-left">
            <div className="pos-title">
              {t("reglement.list") || "Avances / Règlements"}
            </div>

            <div className="pos-kpis">
              <Badge color="primary" pill className="pos-pill">
                {t("stock.totalLines") || "Lignes"} : <span className="ms-1">{totalCount}</span>
              </Badge>
              <Badge color="dark" pill className="pos-pill">
                Total : <span className="ms-1">{formatPrice(totalMontant)}</span>
              </Badge>
            </div>
          </div>

          <div className="pos-toolbar-right">
            <Button
              color="light"
              className="pos-toolbar-btn"
              onClick={handleReload}
              title="Actualiser"
            >
              <FaSearch className="me-2" />
              Refresh
            </Button>

            <Button
              color="primary"
              className="pos-toolbar-btn-primary"
              onClick={() => navigate(PAY_FORM_NEW)}
            >
              <FaPlus className="me-2" />
              Nouvelle avance
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* ====== FILTERS BAR ====== */}
      <Card className="pos-card mt-2">
        <CardBody className="pos-card-body">
          <Row className="g-2 align-items-end">
            <Col lg={6} md={6} sm={12}>
              <FormGroup className="mb-0">
                <Label className="pos-label">{t("reglement.search")}</Label>

                <div className="pos-input-group">
                  <Input
                    innerRef={searchInputRef}
                    value={filters.query}
                    readOnly
                    inputMode="none"
                    placeholder={t("reglement.searchPlaceholder")}
                    className="pos-input text-uppercase"
                    onClick={() => setShowSearchKeypad(true)}
                  />
                  <Button
                    color="primary"
                    type="button"
                    className="pos-side-btn"
                    onClick={() => setShowSearchKeypad(true)}
                  >
                    <FaSearch />
                  </Button>

                  {filters.query ? (
                    <Button
                      color="light"
                      type="button"
                      className="pos-side-btn"
                      onClick={() => {
                        setFilters((f) => ({ ...f, query: "" }));
                        dispatch(reglementActions.avanceClient({ ...filters, query: "" }));
                      }}
                    >
                      <FaTimes />
                    </Button>
                  ) : null}
                </div>
              </FormGroup>
            </Col>

            <Col lg={3} md={3} sm={6} xs={6}>
              <FormGroup className="mb-0">
                <Label className="pos-label">Date début</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  className="pos-input"
                  onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                />
              </FormGroup>
            </Col>

            <Col lg={3} md={3} sm={6} xs={6}>
              <FormGroup className="mb-0">
                <Label className="pos-label">Date fin</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  className="pos-input"
                  onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* ====== TABLE ====== */}
      <Card className="pos-card mt-2">
        <CardBody className="pos-card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">{t("common.loading")}</div>
            </div>
          ) : error ? (
            <div className="p-3 text-danger">
              {t("common.error")} : Erreur lors du chargement des règlements.
            </div>
          ) : (
            <div className="pos-table-wrap">
              <table className="table table-hover table-striped table-bordered mb-0 pos-table">
                <thead className="pos-thead">
                  <tr>
                    <th style={{ width: 160 }}>{t("reglement.reference")}</th>
                    <th>{t("reglement.client")}</th>
                    <th style={{ width: 120 }}>{t("reglement.dateReglement")}</th>
                    <th style={{ width: 140 }} className="text-end">
                      {t("reglement.montantRegle")}
                    </th>
                    <th style={{ width: 160 }}>{t("reglement.modeReglement")}</th>
                    <th style={{ width: 140 }}>{t("reglement.statut")}</th>
                    <th style={{ width: 120 }}>{t("reglement.dateEcheance")}</th>
                    <th style={{ width: 160 }}>{t("common.actions")}</th>
                  </tr>
                </thead>

                <tbody>
                  {(list || []).map((reglement) => (
                    <tr key={reglement.id}>
                      <td>
                        <strong>{reglement.reference || reglement.id}</strong>
                        {reglement.referencePaiement && (
                          <div className="text-muted small">
                            {reglement.referencePaiement}
                          </div>
                        )}
                      </td>

                      <td className="fw-bold">{reglement.nomClient}</td>

                      <td>{new Date(reglement.dateReglement).toLocaleDateString("fr-FR")}</td>

                      <td className="text-end">
                        <strong>{formatPrice(reglement.montantRegle)}</strong>
                        {reglement.montantAffecte > 0 && (
                          <div className="text-muted small">
                            {t("reglement.affecte")}: {formatPrice(reglement.montantAffecte)}
                          </div>
                        )}
                      </td>

                      <td>
                        <span className="me-1">{getModeReglementIcon(reglement.modeReglement)}</span>
                        {t(`reglement.${reglement.modeReglement.toLowerCase()}`)}
                        {reglement.banque && (
                          <div className="text-muted small">
                            {reglement.banque}
                            <br />
                            {reglement.numeroCheque}
                          </div>
                        )}
                      </td>

                      <td>{getStatutBadge(reglement.statut)}</td>

                      <td>
                        {reglement.dateEcheance
                          ? new Date(reglement.dateEcheance).toLocaleDateString("fr-FR")
                          : "-"}
                      </td>

                      <td>
                        <div className="pos-actions">
                          <Button
                            color="warning"
                            size="sm"
                            className="pos-action-btn"
                            title={t("reglement.edit")}
                            onClick={() => handleEdit(reglement)}
                          >
                            <FaEdit className="me-1" />
                            Edit
                          </Button>

                          <Button
                            color="secondary"
                            size="sm"
                            className="pos-action-btn"
                            title={t("reglement.generateReceipt") || "Générer un reçu"}
                            onClick={() => handleGenerateReceipt(reglement)}
                          >
                            <FaReceipt className="me-1" />
                            Reçu
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {(!list || list.length === 0) && (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        <div className="text-muted">{t("reglement.noReglements")}</div>
                      </td>
                    </tr>
                  )}
                </tbody>

                <tfoot>
                  <tr className="bg-light fw-bold">
                    <td colSpan="3" className="text-end">
                      {t("common.totals")}
                    </td>
                    <td colSpan="2">{formatPrice(totalMontant)}</td>
                    <td colSpan="3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ====== SEARCH KEYPAD MODAL ====== */}
      <POSKeypadModal
        isOpen={showSearchKeypad}
        toggle={() => setShowSearchKeypad(false)}
        value={filters.query}
        onChange={(val) => {
          const value = (val || "").toUpperCase();
          setFilters((prev) => ({ ...prev, query: value }));
          debouncedSearch(value);
        }}
        onSearch={() => setShowSearchKeypad(false)}
        title={t("reglement.search") || "Recherche"}
      />
    </Container>
  );
};

export default AvanceClientList;
